/**
 * Голоса озвучки (audioSubmode='tts') — ElevenLabs.
 *
 * Два источника:
 *  • стандартные голоса ElevenLabs (/api/v1/voice/voices) — голос по умолчанию,
 *    вкладка «Стандартные»; многоязычные;
 *  • библиотека голосов (/api/v1/voice/library) — как на elevenlabs.io: язык +
 *    сценарий (рассказы, соцсети, обучение…), носители языка. Голос библиотеки
 *    не из фиксированного списка, поэтому вместе с id в настройках храним имя
 *    (settings.audioVoiceName) — для подписи пилюли.
 * Запросы кэшируются в модуле: один на страницу/фильтр, неудачный повторится.
 *
 * До 02.10.2026 здесь жили 5 пресетов Mureka (Ethan, Victoria…) с сэмплами в
 * /public/voices/. В сохранённых настройках у людей может остаться «Ethan» —
 * такого голоса нет, и пикер показывает голос по умолчанию.
 */
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import { getVoiceLanguages, getVoiceLibrary, getVoices, type VoiceInfo } from '../../../api/voice'

export interface TtsVoice {
	/** voice_id ElevenLabs — уходит в API. */
	id: string
	/** Имя для UI. */
	label: string
	/** Пол по меткам ElevenLabs; null — не указан (значок не рисуем). */
	gender: 'm' | 'f' | null
	/** Короткая подпись по-русски; null — меток нет. */
	hint: string | null
	/** Превью голоса; null — ElevenLabs его не дал (кнопка ▶ не нужна). */
	sample: string | null
	/** Язык голоса библиотеки (ISO 639-1); у стандартных — null. */
	language?: string | null
}

export function toTtsVoice(v: VoiceInfo): TtsVoice {
	return { id: v.id, label: v.name, gender: v.gender, hint: v.hint, sample: v.preview_url, language: v.language ?? null }
}

interface VoicesState {
	voices: TtsVoice[]
	defaultVoice: string | null
	error: string | null
}

let cached: VoicesState | null = null
let inflight: Promise<VoicesState> | null = null

function loadVoices(): Promise<VoicesState> {
	if (cached) return Promise.resolve(cached)
	if (!inflight) {
		inflight = getVoices()
			.then((res): VoicesState => {
				const voices = (res.voices ?? []).map(toTtsVoice)
				const state: VoicesState = {
					voices,
					defaultVoice: res.default_voice ?? voices[0]?.id ?? null,
					error: res.ok && voices.length ? null : res.error || 'Голоса сейчас недоступны',
				}
				if (!state.error) cached = state
				return state
			})
			.catch(() => ({ voices: [], defaultVoice: null, error: 'Голоса сейчас недоступны' }))
			.finally(() => {
				inflight = null
			})
	}
	return inflight
}

/** Голоса озвучки: пока грузятся — пустой список и loading. */
export function useTtsVoices(): VoicesState & { loading: boolean } {
	const [state, setState] = useState<VoicesState | null>(cached)
	useEffect(() => {
		if (cached) return
		let alive = true
		loadVoices().then((s) => {
			if (alive) setState(s)
		})
		return () => {
			alive = false
		}
	}, [])
	return state
		? { ...state, loading: false }
		: { voices: [], defaultVoice: null, error: null, loading: true }
}

/** Подпись выбранного голоса: своё имя у голоса библиотеки (settings.audioVoiceName),
 *  иначе стандартный голос по id, а без него — голос по умолчанию. */
export function useVoiceLabel(currentVoice: string | undefined, currentVoiceName: string | undefined): string {
	const { voices, defaultVoice, loading } = useTtsVoices()
	if (currentVoice && currentVoiceName) return currentVoiceName
	const std = resolveTtsVoice(voices, currentVoice, defaultVoice)
	return std?.label || (loading ? 'Голос…' : 'Голос')
}

/** Голос по id; чужой/устаревший id → голос по умолчанию → первый в списке. */
export function resolveTtsVoice(
	voices: TtsVoice[],
	id: string | null | undefined,
	defaultVoice: string | null,
): TtsVoice | null {
	return (
		voices.find((v) => v.id === id) ||
		voices.find((v) => v.id === defaultVoice) ||
		voices[0] ||
		null
	)
}

// ── Фильтры библиотеки: язык и сценарий ──────────────────────────────

export interface VoiceFilterOptions {
	languages: { code: string; name: string }[]
	useCases: { key: string; label: string }[]
	defaultLanguage: string
}

const FALLBACK_FILTERS: VoiceFilterOptions = {
	languages: [{ code: 'ru', name: 'Russian' }],
	useCases: [
		{ key: 'popular', label: 'Популярные' },
		{ key: 'standard', label: 'Стандартные' },
	],
	defaultLanguage: 'ru',
}

let filtersCached: VoiceFilterOptions | null = null
let filtersInflight: Promise<VoiceFilterOptions> | null = null

function loadFilters(): Promise<VoiceFilterOptions> {
	if (filtersCached) return Promise.resolve(filtersCached)
	if (!filtersInflight) {
		filtersInflight = getVoiceLanguages()
			.then((res) => {
				const opts: VoiceFilterOptions = {
					languages: res.languages?.length ? res.languages : FALLBACK_FILTERS.languages,
					useCases: res.use_cases?.length ? res.use_cases : FALLBACK_FILTERS.useCases,
					defaultLanguage: res.default_language || 'ru',
				}
				if (res.ok) filtersCached = opts
				return opts
			})
			.catch(() => FALLBACK_FILTERS)
			.finally(() => {
				filtersInflight = null
			})
	}
	return filtersInflight
}

/** Имя языка по-русски («be» → «Белорусский»); браузер не знает — имя от ElevenLabs. */
export function languageLabel(code: string, fallback?: string): string {
	try {
		const name = new Intl.DisplayNames(['ru'], { type: 'language' }).of(code)
		if (name && name.toLowerCase() !== code.toLowerCase()) return name.charAt(0).toUpperCase() + name.slice(1)
	} catch {
		// старый браузер или код, которого Intl не знает
	}
	return fallback || code
}

/** Языки для списка: русский первым, остальные по русскому названию. */
export function sortLanguages(langs: { code: string; name: string }[]): { code: string; label: string }[] {
	const items = langs.map((l) => ({ code: l.code, label: languageLabel(l.code, l.name) }))
	return items.sort((a, b) => (a.code === 'ru' ? -1 : b.code === 'ru' ? 1 : a.label.localeCompare(b.label, 'ru')))
}

const FILTERS_KEY = 'ttsVoiceFilters'

function loadSavedFilter(): { language?: string; useCase?: string } {
	try {
		const raw = localStorage.getItem(FILTERS_KEY)
		return raw ? JSON.parse(raw) : {}
	} catch {
		return {}
	}
}

// Выбранные язык и сценарий — общие для окна голоса, шторки и «Диалога»: окно
// «Диалога» смонтировано заранее, и со своим состоянием оно показывало бы
// фильтр, выбранный до последней смены в окне голоса.
let filterState: { language: string; useCase: string } | null = null
const filterListeners = new Set<() => void>()

function getFilterState(): { language: string; useCase: string } {
	if (!filterState) {
		const saved = loadSavedFilter()
		filterState = { language: saved.language || 'ru', useCase: saved.useCase || 'popular' }
	}
	return filterState
}

function setFilterState(next: Partial<{ language: string; useCase: string }>): void {
	filterState = { ...getFilterState(), ...next }
	try {
		localStorage.setItem(FILTERS_KEY, JSON.stringify(filterState))
	} catch {
		// приватное окно — просто не запомним
	}
	filterListeners.forEach((fn) => fn())
}

function subscribeFilter(fn: () => void): () => void {
	filterListeners.add(fn)
	return () => {
		filterListeners.delete(fn)
	}
}

/** Фильтры выбора голоса + выбранные язык и сценарий (общие, запоминаются в браузере). */
export function useVoiceFilters() {
	const [options, setOptions] = useState<VoiceFilterOptions | null>(filtersCached)
	const { language, useCase } = useSyncExternalStore(subscribeFilter, getFilterState, getFilterState)

	useEffect(() => {
		if (filtersCached) return
		let alive = true
		loadFilters().then((o) => {
			if (alive) setOptions(o)
		})
		return () => {
			alive = false
		}
	}, [])

	const setLanguage = useCallback((v: string) => setFilterState({ language: v }), [])
	const setUseCase = useCallback((v: string) => setFilterState({ useCase: v }), [])

	const opts = options ?? FALLBACK_FILTERS
	// Сохранённый сценарий, которого больше нет, — «Популярные».
	const safeUseCase = opts.useCases.some((u) => u.key === useCase) ? useCase : 'popular'
	return { options: opts, loading: !options, language, setLanguage, useCase: safeUseCase, setUseCase }
}

// ── Страницы библиотеки ──────────────────────────────────────────────

interface LibraryState {
	voices: TtsVoice[]
	page: number
	hasMore: boolean
	error: string | null
}

const libraryCache = new Map<string, LibraryState>()

async function fetchLibraryPage(
	language: string,
	useCase: string,
	page: number,
): Promise<{ voices: TtsVoice[]; hasMore: boolean; error: string | null }> {
	try {
		const res = await getVoiceLibrary({ language, use_case: useCase, page })
		if (!res.ok) return { voices: [], hasMore: false, error: res.error || 'Голоса сейчас недоступны' }
		return { voices: (res.voices ?? []).map(toTtsVoice), hasMore: !!res.has_more, error: null }
	} catch {
		return { voices: [], hasMore: false, error: 'Голоса сейчас недоступны' }
	}
}

/** Голоса библиотеки по языку и сценарию: первая страница сразу, дальше — loadMore. */
export function useVoiceLibrary(language: string, useCase: string) {
	const key = `${language}|${useCase}`
	const [state, setState] = useState<{ key: string; data: LibraryState | null; loading: boolean }>(() => ({
		key,
		data: libraryCache.get(key) ?? null,
		loading: !libraryCache.has(key),
	}))

	// Смена фильтра — подстройкой при рендере (react.dev: you might not need an effect).
	if (state.key !== key) {
		setState({ key, data: libraryCache.get(key) ?? null, loading: !libraryCache.has(key) })
	}

	useEffect(() => {
		if (libraryCache.has(key)) return
		let alive = true
		fetchLibraryPage(language, useCase, 0).then((r) => {
			const data: LibraryState = { voices: r.voices, page: 0, hasMore: r.hasMore, error: r.error }
			if (!r.error) libraryCache.set(key, data)
			if (alive) setState({ key, data, loading: false })
		})
		return () => {
			alive = false
		}
	}, [key, language, useCase])

	const loadMore = useCallback(() => {
		const cur = libraryCache.get(key)
		if (!cur || !cur.hasMore) return
		setState((s) => (s.key === key ? { ...s, loading: true } : s))
		fetchLibraryPage(language, useCase, cur.page + 1).then((r) => {
			const seen = new Set(cur.voices.map((v) => v.id))
			const data: LibraryState = {
				voices: [...cur.voices, ...r.voices.filter((v) => !seen.has(v.id))],
				page: r.error ? cur.page : cur.page + 1,
				hasMore: r.error ? cur.hasMore : r.hasMore,
				error: r.error,
			}
			if (!r.error) libraryCache.set(key, data)
			setState((s) => (s.key === key ? { key, data, loading: false } : s))
		})
	}, [key, language, useCase])

	const data = state.key === key ? state.data : null
	return {
		voices: data?.voices ?? [],
		hasMore: !!data?.hasMore,
		error: data?.error ?? null,
		loading: state.key === key ? state.loading : true,
		loadMore,
	}
}

/** Только для тестов: сбросить кэш модуля. */
export function __resetTtsVoicesCache(): void {
	cached = null
	inflight = null
	filtersCached = null
	filtersInflight = null
	filterState = null
	libraryCache.clear()
}
